import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChartBarIcon, CurrencyDollarIcon, ScaleIcon, ShieldCheckIcon, 
  SparklesIcon, ArrowTrendingUpIcon, ClockIcon, DevicePhoneMobileIcon, 
  UserGroupIcon, CheckCircleIcon, StarIcon, BoltIcon, CubeIcon,
  GlobeAltIcon, LockClosedIcon, CpuChipIcon, ChartPieIcon,
  BanknotesIcon, CreditCardIcon, RocketLaunchIcon
} from '@heroicons/react/24/outline';

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeInUp = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };
  const stagger = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* ========== NAVBAR - Glassmorphism ========== */}
      <motion.nav 
        initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/90 backdrop-blur-xl shadow-lg border-b border-gray-100/50' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <CurrencyDollarIcon className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">FinovaTrack</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/login" className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>Sign In</Link>
              <Link to="/register" className="bg-white text-indigo-600 px-5 py-2 rounded-lg text-sm font-semibold hover:shadow-lg transition-all shadow-md">Get Started Free</Link>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* ========== HERO - Premium Gradient ========== */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-pink-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="space-y-8">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <SparklesIcon className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium text-white/90">AI-Powered Financial Intelligence</span>
              </motion.div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight">
                <span className="text-white">Your Money, </span>
                <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">Amplified</span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-lg">
                Experience the future of personal finance with AI-driven insights, real-time tracking, and enterprise-grade security. Built for those who demand excellence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" 
                  className="group bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-indigo-500 hover:to-purple-500 transition-all shadow-2xl shadow-indigo-500/25 hover:shadow-indigo-500/40 text-center">
                  Start Free Trial
                  <span className="ml-2 group-hover:translate-x-1 inline-block transition-transform">→</span>
                </Link>
                <a href="#features" className="border-2 border-white/20 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all text-center backdrop-blur-sm">
                  Explore Features
                </a>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4 text-green-400" /> Free forever</span>
                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4 text-green-400" /> No credit card</span>
                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4 text-green-400" /> 2-min setup</span>
              </div>
              
              {/* Trusted by */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Trusted by finance professionals worldwide</p>
                <div className="flex gap-6 opacity-50">
                  {['Bloomberg', 'Forbes', 'TechCrunch', 'WSJ'].map((name, i) => (
                    <span key={i} className="text-white/60 text-sm font-semibold tracking-wide">{name}</span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Hero Dashboard Preview */}
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-white/40 text-xs ml-2">Financial Overview — July 2026</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white/5 rounded-xl p-4"><p className="text-white/40 text-xs mb-1">Total Balance</p><p className="text-white text-2xl font-bold">$48,250.00</p><p className="text-green-400 text-xs mt-1">↑ 12.5%</p></div>
                  <div className="bg-white/5 rounded-xl p-4"><p className="text-white/40 text-xs mb-1">Monthly Income</p><p className="text-white text-2xl font-bold">$8,420.00</p><p className="text-green-400 text-xs mt-1">↑ 8.3%</p></div>
                </div>
                <div className="bg-white/5 rounded-xl p-4 mb-4">
                  <div className="flex justify-between mb-2"><span className="text-white/60 text-xs">Savings Goal Progress</span><span className="text-white/80 text-xs font-semibold">78%</span></div>
                  <div className="w-full bg-white/10 rounded-full h-2"><motion.div initial={{ width: 0 }} animate={{ width: '78%' }} transition={{ duration: 1.5, delay: 1 }} className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full"></motion.div></div>
                </div>
                <div className="flex gap-2">
                  {['💰', '📊', '🎯', '💎'].map((icon, i) => (
                    <div key={i} className="flex-1 bg-white/5 rounded-lg p-3 text-center hover:bg-white/10 transition-colors cursor-pointer">
                      <span className="text-lg">{icon}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Floating elements */}
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 3 }}
                className="absolute -top-6 -right-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl p-4 shadow-xl">
                <p className="text-white text-xs">📈 +23.5% Growth</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== STATS BAR ========== */}
      <section className="relative -mt-1 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 -mt-12 relative z-10">
            {[
              { value: '50K+', label: 'Active Users', icon: '👥' },
              { value: '$2.4B+', label: 'Transactions Tracked', icon: '💳' },
              { value: '4.9/5', label: 'User Rating', icon: '⭐' },
              { value: '99.99%', label: 'Uptime SLA', icon: '🛡️' },
            ].map((stat, i) => (
              <motion.div key={i} {...fadeInUp} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 text-center hover:-translate-y-1 transition-transform">
                <span className="text-3xl mb-2 block">{stat.icon}</span>
                <p className="text-3xl font-extrabold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">{stat.value}</p>
                <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURES GRID ========== */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Powerful Features</span>
            <h2 className="mt-3 text-5xl font-extrabold text-gray-900">Everything You Need to Build Wealth</h2>
            <p className="mt-4 text-xl text-gray-500 max-w-3xl mx-auto">From AI-powered insights to real-time tracking, FinovaTrack gives you the tools to achieve financial mastery.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🤖', title: 'AI Insights', desc: 'Smart predictions and automated categorization powered by machine learning.' },
              { icon: '📊', title: 'Rich Analytics', desc: 'Beautiful charts, reports, and dashboards that make data come alive.' },
              { icon: '🔒', title: 'Bank-Grade Security', desc: '256-bit encryption, 2FA, and SOC 2 compliant infrastructure.' },
              { icon: '🌍', title: 'Global Ready', desc: 'Multi-currency support with real-time exchange rates for 150+ currencies.' },
              { icon: '⚡', title: 'Real-Time Sync', desc: 'Instant updates across all your devices with cloud synchronization.' },
              { icon: '🎯', title: 'Goal Tracking', desc: 'Set, track, and achieve your financial goals with visual progress tracking.' },
              { icon: '📱', title: 'Mobile First', desc: 'Beautifully designed for every screen size with native mobile experience.' },
              { icon: '🔗', title: 'Integrations', desc: 'Connect with 1000+ banks and financial institutions worldwide.' },
            ].map((f, i) => (
              <motion.div key={i} {...stagger} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center text-2xl mb-5 group-hover:scale-110 transition-transform">{f.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Get Started in Minutes</span>
            <h2 className="mt-3 text-5xl font-extrabold text-gray-900">Three Simple Steps to Financial Freedom</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: '🚀', title: 'Create Your Account', desc: 'Sign up in 30 seconds. No credit card required. Start with our free forever plan.' },
              { step: '02', icon: '📊', title: 'Connect & Track', desc: 'Log your finances manually or connect your bank. AI automatically categorizes everything.' },
              { step: '03', icon: '🎉', title: 'Watch Your Wealth Grow', desc: 'Get AI-powered insights, track goals, and make smarter financial decisions every day.' },
            ].map((s, i) => (
              <motion.div key={i} {...stagger} transition={{ delay: i * 0.2 }} className="text-center relative">
                <div className="text-8xl font-extrabold text-gray-100 absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 select-none">{s.step}</div>
                <div className="relative z-10">
                  <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-6 shadow-xl shadow-indigo-500/25">{s.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
                {i < 2 && <div className="hidden md:block absolute top-16 -right-4 text-3xl text-gray-300">→</div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-5xl font-extrabold text-white">Loved by Financial Leaders</h2>
            <p className="mt-4 text-xl text-indigo-200">Join thousands who have transformed their financial lives</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah Chen', role: 'CFO, TechVentures Inc.', quote: 'FinovaTrack gave us complete visibility into our finances. The AI predictions saved us $200K last year.' },
              { name: 'Marcus Rodriguez', role: 'Founder, GrowthLabs', quote: 'We switched from spreadsheets to FinovaTrack and never looked back. The dashboard is insanely good.' },
              { name: 'Emily Nakamura', role: 'Financial Advisor', quote: 'I recommend FinovaTrack to all my clients. The goal tracking and AI insights are unmatched.' },
            ].map((t, i) => (
              <motion.div key={i} {...stagger} transition={{ delay: i * 0.15 }}
                className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <StarIcon key={j} className="w-5 h-5 text-yellow-400 fill-current" />)}</div>
                <p className="text-white/80 text-lg leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">{t.name[0]}</div>
                  <div><p className="text-white font-semibold">{t.name}</p><p className="text-indigo-300 text-sm">{t.role}</p></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PRICING ========== */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-5xl font-extrabold text-gray-900">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-xl text-gray-500">Start free. Upgrade when you're ready. Cancel anytime.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Starter', price: 'Free', desc: 'Perfect for getting started', features: ['Income & Expense Tracking', 'Basic Dashboard', '5 Budget Categories', 'Monthly Reports'], cta: 'Get Started', popular: false },
              { name: 'Professional', price: '$12/mo', desc: 'For serious money managers', features: ['All Starter Features', 'Unlimited Budgets', 'Savings Goals', 'AI Insights', 'Net Worth Tracking', 'Priority Support'], cta: 'Start Free Trial', popular: true },
              { name: 'Enterprise', price: '$29/mo', desc: 'For power users & teams', features: ['All Pro Features', 'Multi-Currency', 'API Access', 'Custom Reports', 'Team Access', 'Dedicated Support', 'SLA Guarantee'], cta: 'Contact Sales', popular: false },
            ].map((p, i) => (
              <motion.div key={i} {...stagger} transition={{ delay: i * 0.15 }}
                className={`bg-white rounded-2xl p-8 shadow-sm border-2 ${p.popular ? 'border-indigo-500 scale-105 shadow-xl' : 'border-gray-100'} relative`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-1.5 rounded-full text-sm font-semibold">Most Popular</span>}
                <h3 className="text-2xl font-bold text-gray-900 mt-2">{p.name}</h3>
                <p className="text-gray-500 text-sm mt-1">{p.desc}</p>
                <p className="mt-6 text-5xl font-extrabold text-gray-900">{p.price}</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-gray-600 text-sm"><CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />{f}</li>)}
                </ul>
                <Link to="/register" className={`mt-8 block text-center py-3.5 rounded-xl font-semibold transition-all ${p.popular ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{p.cta}</Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <h2 className="text-5xl font-extrabold text-gray-900">Ready to Master Your Money?</h2>
            <p className="mt-6 text-xl text-gray-500">Join 50,000+ users who are building wealth with FinovaTrack. Start free, no credit card required.</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl transition-all shadow-lg shadow-indigo-500/25">Start Free Trial →</Link>
              <Link to="/login" className="border-2 border-gray-200 text-gray-700 px-10 py-4 rounded-xl text-lg font-semibold hover:border-indigo-300 hover:text-indigo-600 transition-all">Sign In</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== WORLD-CLASS FOOTER ========== */}
      <footer className="bg-slate-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                  <CurrencyDollarIcon className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">FinovaTrack</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
                The most advanced financial management platform. Trusted by thousands worldwide to track, analyze, and grow their wealth.
              </p>
              <div className="flex gap-2">
                {['𝕏', '💼', '📸', '🎮'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 bg-slate-800 hover:bg-indigo-600 rounded-lg flex items-center justify-center text-sm transition-all duration-300 hover:-translate-y-1">{icon}</a>
                ))}
              </div>
            </div>
            {[
              { title: 'Product', items: ['Features', 'Pricing', 'Integrations', 'Changelog', 'API Docs'] },
              { title: 'Company', items: ['About Us', 'Blog', 'Careers', 'Press', 'Contact'] },
              { title: 'Resources', items: ['Help Center', 'Community', 'Tutorials', 'Webinars', 'Status'] },
            ].map((col, i) => (
              <div key={i}>
                <h4 className="text-white font-semibold mb-4 text-xs uppercase tracking-wider">{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.items.map((item, j) => (
                    <li key={j}><a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">{item}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
              <p className="text-slate-500 text-sm">© {new Date().getFullYear()} FinovaTrack. All rights reserved.</p>
              <div className="flex gap-6 text-sm text-slate-500">
                <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="hover:text-white transition-colors">Cookie Policy</a>
              </div>
            </div>
            <div className="pt-6 border-t border-slate-800 text-center">
              <p className="text-slate-400 text-sm">
                Designed & Developed with ❤️ by{' '}
                <a href="https://intelliwavve.com" target="_blank" rel="noopener noreferrer" 
                  className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 hover:from-indigo-300 hover:to-purple-300 font-semibold transition-all">
                  Intelliwavve.com
                </a>
              </p>
              <p className="text-slate-600 text-xs mt-2">
                Where Innovation Meets Intelligence — Building tomorrow's financial technology, today.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}